import { Team_Permission } from 'generated/types';
export var PERMISSION_CATEGORIES = [
    {
        id: 'administrative',
        name: 'Administrative & System Access',
        description: 'Administrative features including team management, organization structure, and bulk operations across multiple members.',
        permissions: [
            {
                label: 'Admin Access',
                description: 'Full administrative control including team management and bulk operations',
                primaryPermission: Team_Permission.AccessAdmin,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Read teams & organization groups, create bulk operations (tickets, interactions, deliverables, reassign, status changes)'
            },
            {
                label: 'Organization Launcher',
                description: 'Ability to launch and configure new organizations in the system',
                primaryPermission: Team_Permission.AccessOrgLauncher,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Access to organization launcher features'
            },
            {
                label: 'Demo Creation',
                description: 'Create demonstration environments for testing and training purposes',
                primaryPermission: Team_Permission.AccessDemoCreation,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Ability to create demo accounts and data'
            }
        ]
    },
    {
        id: 'navigation',
        name: 'Navigation',
        description: 'Access to navigation workflows and care coordination. Advanced includes sensitive medical operations.',
        permissions: [
            {
                label: 'Basic Navigation',
                description: 'Core navigation features including forms, deliverables, journal notes, and general care coordination workflows',
                primaryPermission: Team_Permission.AccessNavigationBase,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Forms, deliverables, journal member/ticket notes, doctor/facility profiles, surveys'
            },
            {
                label: 'Advanced Navigation',
                description: 'Sensitive operations including medical journal notes, medication assignments, member status changes, and partner card management',
                primaryPermission: Team_Permission.AccessNavigationAdvanced,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Medical journal notes, medication assignments, member status/history changes, partner cards, claim attachments'
            }
        ]
    },
    {
        id: 'intake',
        name: 'Ticket Routing',
        description: 'Access to intake queue management and tagging system for triaging new member requests.',
        permissions: [
            {
                label: 'Intake Queues',
                description: 'Manage incoming member requests and their routing through intake queues',
                primaryPermission: Team_Permission.ManageIntakeQueues,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Manage intake queues and user assignments'
            },
            {
                label: 'Intake Tags',
                description: 'Categorize and organize intake requests using tags',
                primaryPermission: Team_Permission.ManageIntakeTags,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Manage intake tags for request categorization'
            }
        ]
    },
    {
        id: 'tickets_tasks',
        name: 'Tickets & Tasks',
        description: 'Viewing and editing support tickets and task assignments.',
        permissions: [
            {
                label: 'Tickets',
                description: 'Support tickets for member cases and issues',
                primaryPermission: Team_Permission.ViewTickets,
                secondaryPermission: Team_Permission.ManageTickets,
                primaryDescription: 'Read-only access to tickets',
                secondaryDescription: 'Update and modify tickets'
            },
            {
                label: 'Tasks',
                description: 'Task assignments and tracking for team members',
                primaryPermission: Team_Permission.ViewTasks,
                secondaryPermission: Team_Permission.ManageTasks,
                primaryDescription: 'Read-only access to tasks',
                secondaryDescription: 'Update and modify tasks'
            }
        ]
    },
    {
        id: 'member_management',
        name: 'Member Management',
        description: 'Access to member profile details and ability to modify employee information.',
        permissions: [
            {
                label: 'Member Info',
                description: 'Access to member profile data details',
                primaryPermission: Team_Permission.ViewEmployees,
                secondaryPermission: Team_Permission.ManageEmployees,
                tertiaryPermission: Team_Permission.AdministerEmployees,
                tertiaryPermissionLabel: 'Administer',
                primaryDescription: 'Read-only access to member information',
                secondaryDescription: 'Update and modify member information',
                tertiaryDescription: 'Modify sensitive employee fields (name, identifiers, organization) and resend confirmation email'
            },
            {
                label: 'Invites',
                description: 'Employee invitations email delivery',
                primaryPermission: Team_Permission.ViewInvites,
                secondaryPermission: Team_Permission.CreateInvites,
                primaryDescription: 'Read-only access to the invitations list',
                secondaryDescription: 'Send and resend invitation emails to employees'
            }
        ]
    },
    {
        id: 'user_management',
        name: 'User Management',
        description: 'Access to internal user profiles and ability to modify user account information.',
        permissions: [
            {
                label: 'Users',
                description: 'Health guide and navigator user profiles',
                primaryPermission: Team_Permission.ViewUsers,
                secondaryPermission: Team_Permission.ManageUsers,
                primaryDescription: 'Read-only access to user profiles',
                secondaryDescription: 'Update and modify user profiles'
            }
        ]
    },
    {
        id: 'claims',
        name: 'Claims Management',
        description: 'Access to insurance claims data and ability to modify claim records.',
        permissions: [
            {
                label: 'Claims',
                description: 'Insurance claims processing and status tracking',
                primaryPermission: Team_Permission.ViewClaims,
                secondaryPermission: Team_Permission.ManageClaims,
                primaryDescription: 'Read-only access to insurance claims',
                secondaryDescription: 'Update and modify insurance claim records'
            }
        ]
    },
    {
        id: 'plans',
        name: 'Plans & Benefits',
        description: 'Access to member benefits, insurance, and pharmacy plan details.',
        permissions: [
            {
                label: 'Benefit Manager',
                description: 'Tools for estimating costs and managing member benefits',
                primaryPermission: Team_Permission.AccessBenefitManager,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'Manage insurance plans, benefits, tiers, and cost estimates'
            },
            {
                label: 'Insurance Plans',
                description: 'Member health insurance plan details including coverage and benefits',
                primaryPermission: Team_Permission.ViewInsurancePlans,
                primaryDescription: 'Access to insurance plans'
            },
            {
                label: 'Pharmacy Plans',
                description: 'Prescription drug coverage plans including formularies and cost tiers',
                primaryPermission: Team_Permission.ViewPharmacyPlan,
                secondaryPermission: Team_Permission.ManagePharmacyPlan,
                primaryDescription: 'Read-only access to pharmacy benefit plans',
                secondaryDescription: 'View and update pharmacy benefit plans'
            },
            {
                label: 'PBM Journal Notes',
                description: 'Internal PBM Data Ops notes on tickets, separate from the general Notebook',
                primaryPermission: Team_Permission.AccessPbmJournalNotes,
                primaryPermissionLabel: 'Access',
                primaryDescription: 'View and create PBM notes on tickets'
            }
        ]
    },
    {
        id: 'clinical',
        name: 'Clinical & Care Data',
        description: 'Access to care team assignments and member appointment scheduling.',
        permissions: [
            {
                label: 'Care Pods',
                description: 'Care team assignments',
                primaryPermission: Team_Permission.ViewCarePods,
                primaryDescription: 'Access to care team assignments'
            },
            {
                label: 'Appointments',
                description: 'Member appointment scheduling and history',
                primaryPermission: Team_Permission.ViewAppointments,
                primaryDescription: 'Access to member appointments'
            },
            {
                label: 'Roadmaps',
                description: 'Member care plans and treatment roadmaps',
                primaryPermission: Team_Permission.ViewRoadmaps,
                secondaryPermission: Team_Permission.ManageRoadmaps,
                primaryDescription: 'Read-only access to member care roadmaps',
                secondaryDescription: 'Create and modify member care roadmaps'
            }
        ]
    },
    {
        id: 'communication',
        name: 'Communication & Documents',
        description: 'Access to communication history, member interactions, organization profiles, and uploaded documents.',
        permissions: [
            {
                label: 'Chats',
                description: 'Real-time messaging and chat history with members',
                primaryPermission: Team_Permission.ViewChats,
                secondaryPermission: Team_Permission.ManageChats,
                primaryDescription: 'Read-only access to member chat conversations',
                secondaryDescription: 'Create and modify member chat conversations'
            },
            {
                label: 'Interactions',
                description: 'Communication logs and interaction history with members',
                primaryPermission: Team_Permission.ViewInteractions,
                primaryDescription: 'Access to member interaction history'
            },
            {
                label: 'Organizations',
                description: 'Healthcare organization profiles and information',
                primaryPermission: Team_Permission.ViewOrganizations,
                primaryDescription: 'Access to organization profiles'
            },
            {
                label: 'Uploads',
                description: 'Document management',
                primaryPermission: Team_Permission.ViewUploads,
                primaryDescription: 'Access to uploaded documents'
            }
        ]
    },
    {
        id: 'library',
        name: 'Library',
        description: 'Access to the Library of operational articles, process guides, and product guides assigned to the team.',
        permissions: [
            {
                label: 'Articles',
                description: 'Library articles assigned to the team',
                primaryPermission: Team_Permission.ViewArticles,
                secondaryPermission: Team_Permission.ManageArticles,
                primaryDescription: 'Read-only access to Library articles',
                secondaryDescription: 'Create, edit, and archive Library articles'
            }
        ]
    }
];
